import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DxFormComponent } from 'devextreme-angular';
import { DxButtonTypes } from 'devextreme-angular/ui/button';
import { DxTextBoxTypes } from 'devextreme-angular/ui/text-box';
import { forkJoin } from 'rxjs';
import { DoHttpService } from 'src/app/core/services/do-http.service';
import { LicenseAgreementComponent } from 'src/app/modules/settings/components/organisation-settings-manager/license-agreement/license-agreement.component';
import { ApiUrls } from 'src/app/shared/constants/ApiUrls';
import { COUNTRY_CODE_EXTENDED } from 'src/app/shared/constants/country-code';
import { ProductTypeEnum, RegistrationPageAppDisplayNames } from 'src/app/shared/enum/ProductTypeEnum';
import {
  BillingCycle,
  NewUserRegistrationDto,
  OrganisationRegistrationDto,
  RegistrationPageViewModeEnum,
  SubscriptionPlan,
  SubscriptionState,
  UserAddon,
} from 'src/app/shared/interfaces/ISubscription';
import { ScreenSizeService } from 'src/app/shared/services/screen-size.service';


enum SubscriptionStepWithoutPlans {
  email = 1,
  organizationDetails,
  orderSummary,
  success
}

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrl: './registration.component.scss',
})
export class RegistrationComponent implements OnInit {

  @ViewChild('personalDetailsForm') personalDetailsForm: DxFormComponent;

  // Variables used across all steps
  isSummaryHidden = false;
  stepCount = 4;

  selectedCountryCurrency = 'DKK';
  selectedBillingCycle = 'Monthly';
  countryCurrency = [
    {
      displayExpr: 'DKK',
      currency: 'DKK',
    },
    {
      displayExpr: 'EUR',
      currency: 'EUR',
    },
    // {
    //   displayExpr: 'US Dollar (USD)',
    //   currency: 'USD',
    // },
  ]

  appsEnum = ProductTypeEnum;
  apps = [
    this.appsEnum.DrawoutApp,
    this.appsEnum.Testspace,
    this.appsEnum.LegacyZen,
    this.appsEnum.EplanSketch,
  ];
  appNames = RegistrationPageAppDisplayNames;

  selectedStep = 1;
  subscriptionStep;
  subscriptionState: SubscriptionState = {
    selectedApp: ProductTypeEnum.DrawoutApp,
    viewMode: RegistrationPageViewModeEnum.multi,
    selectedPlans: {
      "1": null,
      "2": null,
      "3": null,
      "4": null,
      "5": null,
      "6": null,
      "7": null,
      "8": null,
      "9": null,
      "10": null,
    },
  };
  isPaymentSelected = false;
  isLicenseAccepted = false;


  // Variables for the plan selection step
  availablePlans: SubscriptionPlan[] = []

  // Variables for the email and personal details step
  emailVerificationFlag: boolean = false;
  registrationForm: FormGroup;
  emailErrorFlag: boolean = false;
  userExistsFlag: boolean = false;
  emailErrorString: string = '';
  passwordMode: DxTextBoxTypes.TextBoxType = 'password';
  confirmPasswordMode: DxTextBoxTypes.TextBoxType = 'password';
  passwordButton: DxButtonTypes.Properties = {
    icon: 'eyeopen',
    stylingMode: 'text',
    onClick: () => {
      this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
    },
  };
  confirmPasswordButton: DxButtonTypes.Properties = {
    icon: 'eyeopen',
    stylingMode: 'text',
    onClick: () => {
      this.confirmPasswordMode = this.confirmPasswordMode === 'text' ? 'password' : 'text';
    },
  };
  countryCodeExtended = COUNTRY_CODE_EXTENDED;
  countryCodeDisplayExpr = (item) => {
    return item === '' ? '' : `${item.country_code} (${item.country_name})`;
  }

  // Variables for the organization details step
  organizationDetails: OrganisationRegistrationDto = {
    address: '',
    city: '',
    companyRegistrationNumber: '',
    contactNumber: '',
    countryCode: '45',
    contactPerson: '',
    country: '',
    email: '',
    organisationName: '',
    zip: '',
    invoiceReferenceNumber: '',
    currency: 'DKK',
  };

  // Variables for the order summary step
  selectedPlans: SubscriptionPlan[] = [];
  totalCost: number = 0;


  userSlabs: UserAddon[] = [];
  currentSlab: UserAddon;
  formSubmissionStatus: 'awaiting' | 'success' | 'failure' = null;
  formSubmissionFailedMessage = 'Some error occured. Please try again later.';

  commonStyle = {
    width: '70%',
    maxWidth: '480px',
    backgroundColor: 'white',
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 20px 6px',
    margin: '0 auto',
    borderRadius: '1rem',
    padding: '1rem',
  };


  constructor(
    private screenSize: ScreenSizeService,
    private request: DoHttpService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
  ) {
    this.goBack = this.goBack.bind(this);
  }

  ngOnInit(): void {
    if (this.isMobile) {
      this.isSummaryHidden = true;
    }

    this.createForm();
    this.getAllPlans();
  }

  // Common functions
  changeSummaryVisibility() {
    if (this.isMobile) {
      this.isSummaryHidden = !this.isSummaryHidden;
    }
  }

  goBack() {
    this.selectedStep -= 1;
  }

  get isMobile() {
    return this.screenSize.isMobile()
  }

  getAllPlans() {
    // the optional subscirptionTierId input indicates the plan which was clicked from drawout's website
    // using that value, we mark the corresponding app as selected and set the value of the subscriptionTierId as the selectedPlan
    // for that app
    const url = ApiUrls.getAllSubscriptionPlans;
    this.request.get(url).subscribe({
      next: (res) => {
        this.availablePlans = res;
        this.availablePlans = this.availablePlans.map(plan => {
          return {
            ...plan,
            addOnCount: 0,
            addOnCost: 0,
            totalUserCount: plan.numberOfUsers,
          }
        });
        this.route.queryParams.subscribe({
          next: (params) => {
            if (params?.planId && params?.currency && params?.billingCycle) {
              if (!this.countryCurrency.map(item => item.currency).includes(params.currency)) {
                this.router.navigate(["/"])
              } else if (+params.billingCycle !== 1 && +params.billingCycle !== 2) {
                this.router.navigate(["/"])
              } else {
                const subscriptionTierId = +params.planId;
                if (this.availablePlans.map(item => item.subscriptionTierId).includes(subscriptionTierId)) {
                  this.stepCount = 3;
                  this.subscriptionStep = SubscriptionStepWithoutPlans;
                  this.selectedCountryCurrency = params.currency;
                  this.selectedBillingCycle = +params.billingCycle === BillingCycle.Monthly ? 'Monthly' : 'Yearly';
                  this.subscriptionState.viewMode = RegistrationPageViewModeEnum.single;
                  const currentlySelectedPlan = this.availablePlans.find(plan => plan.subscriptionTierId === subscriptionTierId);
                  this.selectedPlans.push(currentlySelectedPlan);
                  const appId = currentlySelectedPlan.productType;
                  this.subscriptionState.selectedApp = appId;
                  this.subscriptionState.selectedPlans[appId] = subscriptionTierId;
                  this.getUserSlabs(currentlySelectedPlan.productType);
                  this.calculateTotalUserCount(currentlySelectedPlan);
                  this.calculateAddOnCost(currentlySelectedPlan);
                  this.recalculateOrderTotal();
                } else {
                  this.router.navigate(["/"])
                }
              }
            } else {
              this.router.navigate(["/"])
            }
          }
        })
      },
      error: (res) => {

      }
    })
  }

  getUserSlabs(productType?) {
    if (productType) {
      this.request.get(ApiUrls.getAllUserSlabs(productType)).subscribe(res => {
        this.userSlabs = res;
      })
    } else {
      const requests = [];
      for (let i: number = 1; i <= 9; i++) {
        const getUserSlabsApi = ApiUrls.getAllUserSlabs(i);
        requests.push(this.request.get(getUserSlabsApi));
      }

      forkJoin(requests).subscribe({
        next: (responses) => {
          this.userSlabs = responses.flat();
        }
      });
    }
  }

  createForm() {
    this.registrationForm = new FormGroup({
      userDetails: new FormGroup({
        userName: new FormControl('', [Validators.required, Validators.email]),
        password: new FormControl('', [Validators.minLength(8), Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).+$/)]),
        confirmPassword: new FormControl('', []),
        firstName: new FormControl('', Validators.required),
        lastName: new FormControl('', Validators.required),
        countryCode: new FormControl('45', Validators.required),
        phone: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]+$/)]),
      }),
      organisationDetails: new FormGroup({
        organisationName: new FormControl('', Validators.required),
        companyRegistrationNumber: new FormControl('', Validators.required),
        address: new FormControl('', Validators.required),
        city: new FormControl('', Validators.required),
        zip: new FormControl('', Validators.required),
        country: new FormControl('', Validators.required),
        contactPerson: new FormControl('', Validators.required),
        contactNumber: new FormControl('', Validators.required),
        invoiceReferenceNumber: new FormControl(''),
        email: new FormControl('', [Validators.required, Validators.email])
      })
    }, { validators: this.passwordMatchValidator });
  }

  get filteredPlans(): SubscriptionPlan[] {
    return this.availablePlans.filter(plan =>
      plan.productType === this.subscriptionState.selectedApp
    )
  }

  selectedCountryCurrencyChanged() {
    this.selectedPlans.forEach(plan => {
      this.calculateTotalUserCount(plan);
      this.calculateAddOnCost(plan);
    });
    this.recalculateOrderTotal();
  }

  onBillingCycleChanged(e) {
    this.selectedBillingCycle = e;
    this.selectedPlans.forEach(plan => {
      this.calculateTotalUserCount(plan);
      this.calculateAddOnCost(plan);
    });
    this.recalculateOrderTotal();
  }


  subscriptionStateChanged(event) {
    this.selectedPlans = [];
    this.subscriptionState = event;
    const selectedPlan = Object.entries(this.subscriptionState.selectedPlans)
      .filter(([key, value]) => value !== null)[0];
    const currentlySelectedPlan = this.availablePlans.find(plan => plan.subscriptionTierId === selectedPlan[1]);
    this.selectedPlans.push(currentlySelectedPlan);
    this.calculateTotalUserCount(currentlySelectedPlan);
    this.calculateAddOnCost(currentlySelectedPlan);
    this.recalculateOrderTotal();
  }

  savePlanDetails() {
    const isPlanSelected = Object.values(this.subscriptionState.selectedPlans).every(value => value === null);
    if (isPlanSelected) {
    } else {
      this.selectedStep += 1;
    }
  }

  verifyEmailAddress(flag, avoidNext = false) {
    const userNameControl = this.registrationForm.get('userDetails.userName');

    if (!userNameControl || userNameControl.invalid) {
      userNameControl?.markAsTouched();
      userNameControl?.markAsDirty();
      this.emailErrorFlag = true;
      this.userExistsFlag = false;
      this.emailErrorString = 'Invalid Email Address! Please enter a valid email address';
      return;
    }

    this.emailErrorFlag = false;
    this.userExistsFlag = false;

    let email = userNameControl.value;
    const url = ApiUrls.verifyEmail(email);

    if (flag) {
      if (this.registrationForm.get('userDetails').invalid) return;
      else {
        this.emailErrorFlag = false;
        this.userExistsFlag = false;
        const formData = this.registrationForm.get('userDetails').value;
        const emailVerificationUrl = ApiUrls.verifyEmail(formData.userName);
        this.request.get(emailVerificationUrl).subscribe({
          next: (res) => {
            if (res.userExsist) {
              this.emailErrorFlag = true;
              this.userExistsFlag = true;
              this.emailErrorString = 'Email address already exists! Use a different email address or ';
              return
            } else {
              this.emailVerificationFlag = true;
              this.organizationDetails.email = formData.userName;
              this.organizationDetails.contactPerson = `${formData.firstName} ${formData.lastName}`;
              this.organizationDetails.contactNumber = `${formData.phone}`;
              this.organizationDetails.countryCode = `${formData.countryCode}`;
              if (!avoidNext) {
                this.selectedStep += 1;
              }
            }
          },
          error: (res) => {
            console.error(res)
          },
        })

      }
    } else {
      this.request.get(url).subscribe({
        next: (res) => {
          if (res.userExsist) {
            this.emailErrorFlag = true;
            this.userExistsFlag = true;
            this.emailErrorString = 'Email address already exists! Use a different email address or ';
          } else {
            this.emailVerificationFlag = true;
          }
        },
        error: (res) => {
          console.error(res)
        },
      })
    }
  }

  isVerifyEmailButtonDisabled() {
    // if (!this.emailVerificationFlag) {
    //   return false
    // } else {
    if (
      // !this.registrationForm.get('userDetails.password')?.value ||
      // !this.registrationForm.get('userDetails.confirmPassword')?.value ||
      // this.registrationForm.hasError('mismatch') ||
      this.registrationForm.get('userDetails').invalid
    ) {
      return true
    } else return false
    // }
  }

  showPasswordErrors() {
    const passwordControl = this.registrationForm.get('userDetails.password');
    const confirmPasswordControl = this.registrationForm.get('userDetails.confirmPassword');

    const passwordTouched = passwordControl?.touched || passwordControl?.dirty;
    const confirmPasswordTouched = confirmPasswordControl?.touched || confirmPasswordControl?.dirty;

    const bothFieldsFilled = passwordControl?.value && confirmPasswordControl?.value;

    const showFieldErrors =
      (passwordTouched && passwordControl?.errors) &&
      (confirmPasswordTouched && confirmPasswordControl?.errors);

    const showMismatchError =
      bothFieldsFilled && this.registrationForm.hasError('mismatch');

    return showMismatchError || showFieldErrors;
  }

  passwordMatchValidator(g: FormGroup) {
    const password = g.get('userDetails.password')?.value;
    const confirmPassword = g.get('userDetails.confirmPassword')?.value;
    return password === confirmPassword ? null : { mismatch: true };
  }

  saveOrganizationDetails(e) {
    const {
      countryCode,
      contactNumber,
      contactPerson,
      ...organizationData
    } = e;

    this.registrationForm.get('organisationDetails').patchValue({
      ...organizationData,
      contactPerson: contactPerson,
      contactNumber: `${countryCode}${contactNumber}`
    });

    const nameParts = contactPerson.trim().split(/\s+/);
    this.registrationForm.get('userDetails').patchValue({
      firstName: nameParts[0],
      lastName: nameParts.slice(1).join(' '),
      countryCode: countryCode,
      phone: contactNumber
    });
    this.selectedStep += 1;
  }


  updateAddOnUsers(plan: SubscriptionPlan, diff: number, e?: Event) {
    if (diff === -1 && plan.addOnCount === 0) return;
    plan.addOnCount = plan.addOnCount + diff;
    this.calculateTotalUserCount(plan);
    this.calculateAddOnCost(plan);
    this.recalculateOrderTotal();
  }

  calculateTotalUserCount(plan: SubscriptionPlan) {
    plan.totalUserCount = plan.numberOfUsers + plan.addOnCount;
  }

  calculateAddOnCost(plan?: SubscriptionPlan) {
    if (!plan || !(plan.addOnCount >= 0)) return;
    let matchedSlab: UserAddon | undefined;
    for (const slab of this.userSlabs) {
      if (slab.productType !== plan.productType) continue;

      if (slab.userRangeTo === null || slab.unlimitedUsers || plan.totalUserCount <= slab.userRangeTo) {
        matchedSlab = slab;
        break;
      }
    }
    if (!matchedSlab) return;
    plan.addOnSlab = matchedSlab;

    const addOnRates = plan.addOnSlab.userSlabRates.find(slabRate => slabRate.currency === this.selectedCountryCurrency);
    const addOnRate = this.selectedBillingCycle === 'Monthly' ? addOnRates.monthlyPrice : addOnRates.yearlyPrice;
    plan.addOnRate = addOnRate;
    plan.addOnCost = plan.addOnCount * addOnRate;
  }

  recalculateOrderTotal() {
    let total = 0;
    for (const plan of this.selectedPlans) {
      let rates = plan.tierRates.find(rate => rate.currency === this.selectedCountryCurrency);
      let rate = this.selectedBillingCycle === 'Monthly' ? rates.monthlyRate : rates.yearlyRate;
      plan.rate = rate;
      total += rate + plan.addOnCost;
    }
    this.totalCost = total;
  }

  getAddOnUserPricing(plan: SubscriptionPlan) {
    let pricingText = [`Add-on user pricing is determined based on the total user count (i.e., plan users + add-on users).`, `Pricing details:`];

    this.userSlabs
      .filter(slab => slab.productType === plan.productType)
      .sort((a, b) => {
        // Sort slabs by range: defined range first, then unlimited
        if (a.userRangeTo === null) return 1;
        if (b.userRangeTo === null) return -1;
        return a.userRangeTo - b.userRangeTo;
      })
      .forEach((slab, index, array) => {
        const rate = slab.userSlabRates.find(rate => rate.currency === this.selectedCountryCurrency);
        if (!rate) return;

        let rangeText = '';
        if (slab.userRangeTo !== null) {
          const lowerBound = index === 0 ? 1 : array[index - 1].userRangeTo + 1;
          rangeText = `${lowerBound}–${slab.userRangeTo} users`;
        } else {
          const prevUpper = array[index - 1]?.userRangeTo ?? 0;
          rangeText = `More than ${prevUpper} users`;
        }

        if (this.selectedBillingCycle === 'Monthly') pricingText.push(`• ${rangeText}: ${rate.monthlyPrice} ${this.selectedCountryCurrency}/month per add-on user`);
        else if (this.selectedBillingCycle === 'Yearly') pricingText.push(`• ${rangeText}: ${rate.yearlyPrice} ${this.selectedCountryCurrency}/year per add-on user`);
      });

    return pricingText;
  }

  openLicenseAgreement(e) {
    const dialogRef = this.dialog.open(LicenseAgreementComponent, {
      width: '100%',
      height: '90%',
      panelClass: ['animate__animated', 'animate__faster', 'animate__fadeInUp', 'rounded-0'],
      autoFocus: false,
      data: { isNewUserRegistration: true }
    });
  }

  submitForm() {
    if (this.registrationForm.invalid) {
      return;
    }

    const selectedPlan = this.selectedPlans[0];

    const formValue = this.registrationForm.value;
    const data: NewUserRegistrationDto = {
      subscriptionTierId: selectedPlan.subscriptionTierId,
      additionalUsers: selectedPlan.addOnCount,
      billingCycle: this.selectedBillingCycle === 'Monthly' ? BillingCycle.Monthly : BillingCycle.Annually,
      userDetails: {
        userName: formValue.userDetails?.userName || '',
        password: formValue.userDetails?.password || '',
        firstName: formValue.userDetails?.firstName || '',
        lastName: formValue.userDetails?.lastName || '',
        phone: formValue.userDetails?.phone || '',
        countryCode: formValue.userDetails?.countryCode || '45',
        roleId: 0,
        companyName: formValue.organisationDetails?.organisationName || '',
        companyRegistrationNumber: formValue.organisationDetails?.companyRegistrationNumber || ''
      },
      organisationDetails: {
        organisationName: formValue.organisationDetails?.organisationName || '',
        companyRegistrationNumber: formValue.organisationDetails?.companyRegistrationNumber || '',
        address: formValue.organisationDetails?.address || '',
        city: formValue.organisationDetails?.city || '',
        zip: formValue.organisationDetails?.zip || '',
        country: formValue.organisationDetails?.country || '',
        contactPerson: formValue.organisationDetails?.contactPerson || '',
        contactNumber: formValue.organisationDetails?.contactNumber || '',
        email: formValue.organisationDetails?.email || '',
        invoiceReferenceNumber: formValue.organisationDetails?.invoiceReferenceNumber || '',
        currency: this.selectedCountryCurrency || 'DKK'
      }
    };

    const url = ApiUrls.registerUser;
    this.formSubmissionStatus = 'awaiting';
    this.request.post(url, data).subscribe({
      next: (res) => {
        if (res.success) {
          this.selectedStep += 1;
          this.formSubmissionStatus = 'success';
        } else {
          this.formSubmissionFailedMessage = res.message;
          this.formSubmissionStatus = 'failure';
        }
      },
      error: (res) => {
        this.formSubmissionFailedMessage = 'Some error occured. Please try again later.'
        this.formSubmissionStatus = 'failure';
      }
    })
  }

  goToLoginPage() {
    this.router.navigate(['/'])
  }
}
